import { styled, useTheme } from '@mui/material/styles';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { TextMDSemiBold, TextSMRegular } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { TrashIcon } from '@/icons/TrashIcon';
import { EditIcon } from '@/icons/EditIcon';
import { staticColors } from '@/lib/theme';
import { CheckIcon } from '@/icons/CheckIcon';
import { UploadDocResponse } from '@/services/PaymentsServiceV2';

interface UploadAreaProps {
    label: string;
    successLabel?: string;
    image: string;
    description: string;
    isMarkingDone?: boolean;
    isChangeDisabled?: boolean;
    signaturePageNumber?: number;
    onFileUploaded?: () => void;
    onHandleDiscard?: () => void;
    handleSign?: (file: UploadDocResponse) => void;
}

interface StyledUploadContainerProps {
    dragOver: boolean;
}

const StyledUploadContainer = styled(Box)<StyledUploadContainerProps>(
    ({ theme, dragOver }) => ({
        // width: '402px',
        marginTop: theme.spacing(1),
        border: '1px solid #EAECF0',
        cursor: 'pointer',
        backgroundColor: dragOver
            ? theme.palette.primary.light + '0a'
            : 'transparent',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.grey[50]
        },
        height: 48,
        gap: 16,
        borderRadius: 8,
        padding: 8
    })
);

const StyledFlagContainer = styled(Box)(({ theme }) => ({
    width: 32,
    height: 32,
    // marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}));

const StyledFlagImage = styled('img')({
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover'
});

const StyledHiddenInput = styled('input')({
    display: 'none'
});

const UI_STATE = {
    INITIAL: 'initial',
    UPLOADED: 'uploaded',
    MARKING_DONE: 'marking_done',
    CHANGE_DISABLED: 'uploaded_change_disabled'
};

export const UploadArea = ({
    label,
    image,
    description,
    successLabel,
    isChangeDisabled,
    signaturePageNumber,
    onFileUploaded,
    isMarkingDone,
    onHandleDiscard,
    handleSign
}: UploadAreaProps) => {
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { documentUpload } = usePaymentsMutations();
    const [fileUploaded, setFileUploaded] = useState<boolean>(true);

    const [uiState, setUiState] = useState<string>(UI_STATE.INITIAL);

    useEffect(() => {
        setUiState(UI_STATE.INITIAL);
    }, []);

    useEffect(() => {
        if (isChangeDisabled) {
            setUiState(UI_STATE.CHANGE_DISABLED);
        }
    }, [isChangeDisabled]);

    useEffect(() => {
        if (documentUpload.isSuccess) {
            setUiState(UI_STATE.UPLOADED);
        }
    }, [documentUpload.isSuccess]);

    useEffect(() => {
        documentUpload.reset();
        // setUiState(UI_STATE.INITIAL);
    }, []);

    useHandleResponse({
        error: documentUpload.error
    });

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            // onFileUpload(files[0]);
            documentUpload.mutate(files[0]);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            // onFileUpload(files[0]);
            documentUpload.mutate(files[0]);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleClickSign = () => {
        handleSign?.(documentUpload?.data?.data!);
    };

    useEffect(() => {
        if (isMarkingDone) {
            setUiState(UI_STATE.MARKING_DONE);
        }
    }, [isMarkingDone]);

    const { t } = useTranslation();
    const theme = useTheme();

    const discard = () => {
        setUiState(UI_STATE.INITIAL);
        onHandleDiscard?.();
    };

    return (
        <StyledUploadContainer
            style={{
                ...([UI_STATE.CHANGE_DISABLED, UI_STATE.MARKING_DONE].includes(
                    uiState
                )
                    ? { backgroundColor: staticColors.successLight }
                    : {})
            }}
            dragOver={dragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {!documentUpload.isPending && (
                <>
                    <StyledHiddenInput
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        onChange={handleFileSelect}
                    />

                    <StyledFlagContainer>
                        <StyledFlagImage src={image} alt="Country flag" />
                    </StyledFlagContainer>

                    <Box style={{ marginRight: 'auto' }}>
                        {[UI_STATE.INITIAL].includes(uiState) && (
                            <TextSMRegular>{label}</TextSMRegular>
                        )}
                        {uiState == UI_STATE.UPLOADED && (
                            <TextSMRegular
                                fontWeight={'bold'}
                                noWrap
                                color={theme.palette.text.success}
                            >
                                {successLabel}
                            </TextSMRegular>
                        )}

                        {[UI_STATE.UPLOADED, UI_STATE.INITIAL].includes(
                            uiState
                        ) && (
                            <TextSMRegular color={theme.palette.grey[500]}>
                                {description}
                            </TextSMRegular>
                        )}

                        {[
                            UI_STATE.MARKING_DONE,
                            UI_STATE.CHANGE_DISABLED
                        ].includes(uiState) && (
                            <>
                                <TextSMRegular
                                    fontWeight={'bold'}
                                    noWrap
                                    color={theme.palette.text.success}
                                >
                                    {successLabel}
                                </TextSMRegular>
                                <TextSMRegular
                                    color={theme.palette.text.success}
                                >
                                    {t('documents.signature_set_on_page', {
                                        x: signaturePageNumber || 1
                                    })}
                                </TextSMRegular>
                            </>
                        )}
                    </Box>
                    {uiState == UI_STATE.INITIAL && (
                        <Button
                            onClick={handleClick}
                            variant="text"
                            style={{ marginLeft: 'auto' }}
                        >
                            <TextMDSemiBold
                                mr={1}
                                color="primary"
                                textTransform={'none'}
                            >
                                {t('upload')}
                            </TextMDSemiBold>
                            <FileUploadOutlinedIcon fontSize={'large'} />
                        </Button>
                    )}

                    {uiState == UI_STATE.UPLOADED && (
                        <>
                            <Button
                                variant="text"
                                style={{ marginLeft: 'auto' }}
                                onClick={handleClickSign}
                            >
                                <TextMDSemiBold
                                    mr={1}
                                    color="primary"
                                    textTransform={'none'}
                                    noWrap
                                >
                                    {t('documents.signature_area')}
                                </TextMDSemiBold>
                                <EditIcon />
                            </Button>
                        </>
                    )}

                    {(uiState == UI_STATE.MARKING_DONE ||
                        uiState == UI_STATE.UPLOADED) && (
                        <IconButton
                            onClick={() => {
                                onHandleDiscard?.();
                                setUiState(UI_STATE.INITIAL);
                            }}
                            size="small"
                        >
                            <TrashIcon />
                        </IconButton>
                    )}
                    {uiState == UI_STATE.CHANGE_DISABLED && <CheckIcon />}
                </>
            )}

            {documentUpload.isPending && <CircularProgress size={16} />}
        </StyledUploadContainer>
    );
};
